import React from 'react';
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Icons } from '@marageti/z4-lib';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { Ticket, TicketStatus } from '@marageti/z4-sdk/lib/tickets';
import {
  EditableTicket, TicketFormData, AssigneeOption, Option,
} from './options';
import { TicketCloseFormData } from '../../components/modals/ticket-close-modal';
import { TicketReassignFormData } from '../../components/modals/ticket-reassign-modal';
import TicketList from '../../components/ticket-list';
import TicketsFilters from './tickets-filters';

type TicketsViewProps = {
  // Data
  tickets: Ticket[];
  totalHits: number;
  isLoading: boolean;
  error: ApiError | undefined;
  hasMore: boolean;
  loadMore: () => void;
  // Filters
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedTypes: Option[];
  onTypeChange: (event: any, values: Option[]) => void;
  assignees: AssigneeOption[];
  selectedAssignee: AssigneeOption;
  onAssigneeChange: (assignee: AssigneeOption | null) => void;
  sortValue: string;
  onSortChange: (sort: string) => void;
  selectedStatusChips: string[];
  onStatusChipChange: (chips: string[]) => void;
  selectedSeverityChips: string[];
  onSeverityChipChange: (chips: string[]) => void;
  isSeverityChipsDisabled: boolean;
  // Actions
  onOpenCreate: () => void;
  onEditTicket: (ticket: Ticket) => void;
  onReassignTicket: (ticket: Ticket) => void;
  onDeleteTicket: (ticket: Ticket) => void;
  onStatusChange: (ticketId: string, status: TicketStatus) => void;
  // Modal
  isModalOpen: boolean;
  editingTicket?: EditableTicket;
  defaultAssigneeIds?: string[];
  onCloseModal: () => void;
  onSaveTicket: (data: TicketFormData) => Promise<void>;
  // Close modal
  isCloseModalOpen: boolean;
  onCloseCloseModal: () => void;
  onSaveClose: (data: TicketCloseFormData) => Promise<void>;
  // Reassign modal
  isReassignModalOpen: boolean;
  reassigningTicketAssigneeIds: string[];
  onCloseReassignModal: () => void;
  onSaveReassign: (data: TicketReassignFormData) => Promise<void>;
  // Delete modal
  isDeleteModalOpen: boolean;
  onCloseDeleteModal: () => void;
  onConfirmDelete: () => Promise<void>;
};

const TicketsView = ({
  // Data
  tickets,
  totalHits,
  isLoading,
  error,
  hasMore,
  loadMore,
  // Filters
  searchValue,
  onSearchChange,
  selectedTypes,
  onTypeChange,
  assignees,
  selectedAssignee,
  onAssigneeChange,
  sortValue,
  onSortChange,
  selectedStatusChips,
  onStatusChipChange,
  selectedSeverityChips,
  onSeverityChipChange,
  isSeverityChipsDisabled,
  // Actions
  onOpenCreate,
  onEditTicket,
  onReassignTicket,
  onDeleteTicket,
  onStatusChange,
  // Modal
  isModalOpen,
  editingTicket,
  defaultAssigneeIds,
  onCloseModal,
  onSaveTicket,
  // Close modal
  isCloseModalOpen,
  onCloseCloseModal,
  onSaveClose,
  // Reassign modal
  isReassignModalOpen,
  reassigningTicketAssigneeIds,
  onCloseReassignModal,
  onSaveReassign,
  // Delete modal
  isDeleteModalOpen,
  onCloseDeleteModal,
  onConfirmDelete,
}: TicketsViewProps) => (
  <Container maxWidth="xl">
    <Box py={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography component="h1" variant="h2">
          Tickets
        </Typography>
        <Button
          variant="contained"
          onClick={onOpenCreate}
          role="button"
          sx={{
            minWidth: 'fit-content',
            display: { xs: 'none', md: 'block' },
          }}
        >
          New Ticket
        </Button>
        <IconButton
          onClick={onOpenCreate}
          sx={{ display: { xs: 'block', md: 'none' } }}
        >
          <Icons.PlusCircleOutlined color="primary" />
        </IconButton>
      </Stack>

      <TicketsFilters
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        selectedTypes={selectedTypes}
        onTypeChange={onTypeChange}
        assignees={assignees}
        selectedAssignee={selectedAssignee}
        onAssigneeChange={onAssigneeChange}
        sortValue={sortValue}
        onSortChange={onSortChange}
        selectedStatusChips={selectedStatusChips}
        onStatusChipChange={onStatusChipChange}
        selectedSeverityChips={selectedSeverityChips}
        onSeverityChipChange={onSeverityChipChange}
        isSeverityChipsDisabled={isSeverityChipsDisabled}
      />

      <TicketList
        tickets={tickets}
        totalHits={totalHits}
        isLoading={isLoading}
        error={error}
        hasMore={hasMore}
        loadMore={loadMore}
        onEditTicket={onEditTicket}
        onReassignTicket={onReassignTicket}
        onDeleteTicket={onDeleteTicket}
        onStatusChange={onStatusChange}
        isModalOpen={isModalOpen}
        editingTicket={editingTicket}
        assignees={assignees}
        defaultAssigneeIds={defaultAssigneeIds}
        onCloseModal={onCloseModal}
        onSaveTicket={onSaveTicket}
        isCloseModalOpen={isCloseModalOpen}
        onCloseCloseModal={onCloseCloseModal}
        onSaveClose={onSaveClose}
        isReassignModalOpen={isReassignModalOpen}
        reassigningTicketAssigneeIds={reassigningTicketAssigneeIds}
        onCloseReassignModal={onCloseReassignModal}
        onSaveReassign={onSaveReassign}
        isDeleteModalOpen={isDeleteModalOpen}
        onCloseDeleteModal={onCloseDeleteModal}
        onConfirmDelete={onConfirmDelete}
      />
    </Box>
  </Container>
);

export default TicketsView;
